import { useEffect, useRef, useState } from 'react';
import './BottomSheet.css';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CLOSE_DISTANCE = 120;      // dragged this far down: close
const FLICK_VELOCITY = 0.6;      // px/ms: a quick flick closes from much less
const EXIT_MS = 260;

// A bottom sheet the way phones do it: slides up, backdrop, drag the handle (or
// flick) down to dismiss, Escape and backdrop tap also close. Focus moves into
// it and returns to whatever opened it; the page behind doesn't scroll.
export default function BottomSheet({ open, onClose, title, label, children }) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const sheetRef = useRef(null);
  const openerRef = useRef(null);
  const drag = useRef(null);

  // Mount, then animate in on the next frame; animate out, then unmount.
  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement;
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    const timer = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // Lock page scroll while open; hand focus back afterwards.
  useEffect(() => {
    if (!mounted) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sheetRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [mounted]);

  if (!mounted) return null;

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = [...sheetRef.current.querySelectorAll(FOCUSABLE)];
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === sheetRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Dragging writes the transform straight to the element: a re-render per
  // pointer move would make the drag stutter.
  const onPointerDown = (e) => {
    drag.current = { startY: e.clientY, startT: performance.now(), dy: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
    sheetRef.current.style.transition = 'none';
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.startY);
    sheetRef.current.style.transform = `translateY(${drag.current.dy}px)`;
  };

  const onPointerUp = () => {
    if (!drag.current) return;
    const { dy, startT } = drag.current;
    const velocity = dy / Math.max(1, performance.now() - startT);
    drag.current = null;
    sheetRef.current.style.transition = '';
    sheetRef.current.style.transform = '';
    if (dy > CLOSE_DISTANCE || (velocity > FLICK_VELOCITY && dy > 30)) onClose();
  };

  return (
    <div className={`sheet-root ${visible ? 'is-visible' : ''}`}>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={label || title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div
          className="sheet-grip"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="sheet-handle" aria-hidden="true" />
          {title && <h2 className="sheet-title">{title}</h2>}
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}
